// Mirrors admin-service `src/shared/enums/fee.enums.ts`, which is itself copied
// from broker-db-service. The API serialises these as raw column values, so the
// screen keeps the wire value and looks the human label up here.

export const FEE_LIFECYCLE_STAGE = {
  ONBOARDING: 'onboarding',
  PRIMARY_SELL: 'primary_sell',
  SECONDARY_SELL: 'secondary_sell',
  SECONDARY_BUY: 'secondary_buy',
  RUNNING_COST: 'running_cost',
  FINAL_EXIT: 'final_exit',
} as const

export type FeeLifecycleStage = (typeof FEE_LIFECYCLE_STAGE)[keyof typeof FEE_LIFECYCLE_STAGE]

export const FEE_LIFECYCLE_STAGE_LABEL: Record<FeeLifecycleStage, string> = {
  onboarding: 'Onboarding',
  primary_sell: 'Primary Sale',
  secondary_sell: 'Secondary Sale',
  secondary_buy: 'Secondary Purchase',
  running_cost: 'Running Cost',
  final_exit: 'Final Exit',
}

/**
 * Where each stage's fees actually bite. Written for the operator looking at a
 * property's fee list, not for the schema: the stage decides *when* a fee is
 * charged, and where a one-time fee's money comes from: a property-owner fee is
 * netted from the seller's proceeds, and an investor's one-time purchase cost
 * lands in the offering price (Fee Model v8).
 */
export const FEE_LIFECYCLE_STAGE_DESCRIPTION: Record<FeeLifecycleStage, string> = {
  onboarding:
    "Charged once while the property is onboarded, before it is listed. A fee the property owner pays here is deducted from the seller's proceeds at settlement and never reaches the investor's price.",
  primary_sell:
    'Charged on the first sale of tokens to investors. A one-time purchase cost investors pay here (government charges, acquisition cost) is part of the offering price; the platform fee and payment-gateway charges are charged on the order instead.',
  secondary_sell:
    'Charged to the seller when an investor sells tokens on to another investor, settled per trade.',
  secondary_buy:
    'Charged to the buyer when tokens change hands between investors, settled per trade.',
  running_cost:
    'Recurring for as long as the property is held — settled periodically against rental income rather than at a transaction.',
  final_exit:
    'Charged when the property is sold in full and the offering is wound down, applied to the exit proceeds.',
}

export const FEE_PARTY_ROLE = {
  INVESTOR: 'investor',
  PROPERTY_OWNER: 'property_owner',
  PLATFORM: 'platform',
  SLICEAL: 'sliceal',
  GOVERNMENT_AUTHORITY: 'government_authority',
  PAYMENT_GATEWAY: 'payment_gateway',
  PMC: 'pmc',
  EXTERNAL: 'external',
} as const

export type FeePartyRole = (typeof FEE_PARTY_ROLE)[keyof typeof FEE_PARTY_ROLE]

export const FEE_PARTY_ROLE_LABEL: Record<FeePartyRole, string> = {
  investor: 'Investor',
  property_owner: 'Property Owner',
  platform: 'Platform',
  sliceal: 'Sliceal',
  government_authority: 'Government Authority',
  payment_gateway: 'Payment Gateway',
  pmc: 'PMC',
  external: 'External Party',
}

export const FEE_RECURRENCE = {
  ONE_TIME: 'one_time',
  ANNUAL: 'annual',
} as const

export type FeeRecurrence = (typeof FEE_RECURRENCE)[keyof typeof FEE_RECURRENCE]

export const FEE_RECURRENCE_LABEL: Record<FeeRecurrence, string> = {
  one_time: 'One-time',
  annual: 'Annual',
}

export const FEE_VALUE_TYPE = {
  PERCENTAGE: 'percentage',
  FLAT: 'flat',
  RATE_PER_UNIT: 'rate_per_unit',
} as const

export type FeeValueType = (typeof FEE_VALUE_TYPE)[keyof typeof FEE_VALUE_TYPE]

export const FEE_VALUE_TYPE_LABEL: Record<FeeValueType, string> = {
  percentage: 'Percentage',
  flat: 'Flat (AED)',
  rate_per_unit: 'Rate per area',
}

// The measurement a `rate_per_unit` fee is charged against, and the unit the
// rate is quoted in. The unit is part of the value so a rate can never be
// applied against the wrong one.
//
// Everything the platform records is in square metres — the property forms, the
// property facts, the financials all speak sqm — so the sqm members are the ones
// a fee should use. The sqft members exist because the column carries them; a
// definition on one is quoted per square foot and its quantity is converted
// server-side when the snapshot is written.
export const FEE_UNIT_BASIS = {
  BUILT_UP_AREA_SQFT: 'built_up_area_sqft',
  BUILT_UP_AREA_SQM: 'built_up_area_sqm',
  ACTUAL_AREA_SQFT: 'actual_area_sqft',
  ACTUAL_AREA_SQM: 'actual_area_sqm',
  COMMON_AREA_SQM: 'common_area_sqm',
  SHARED_AREA_SQM: 'shared_area_sqm',
} as const

export type FeeUnitBasis = (typeof FEE_UNIT_BASIS)[keyof typeof FEE_UNIT_BASIS]

export const FEE_UNIT_BASIS_LABEL: Record<FeeUnitBasis, string> = {
  built_up_area_sqft: 'Built-up area (per sq ft)',
  built_up_area_sqm: 'Built-up area (per sqm)',
  actual_area_sqft: 'Actual area (per sq ft)',
  actual_area_sqm: 'Actual area (per sqm)',
  common_area_sqm: 'Common area (per sqm)',
  shared_area_sqm: 'Shared area (per sqm)',
}

/** Just the unit, for suffixing a rate: `AED 193.75 / sqm`. */
export const FEE_UNIT_BASIS_UNIT: Record<FeeUnitBasis, string> = {
  built_up_area_sqft: 'sq ft',
  built_up_area_sqm: 'sqm',
  actual_area_sqft: 'sq ft',
  actual_area_sqm: 'sqm',
  common_area_sqm: 'sqm',
  shared_area_sqm: 'sqm',
}

/**
 * The unit a server-sent basis is measured in ("sq ft" / "sqm"). Falls back to
 * sqm — the platform standard — when the basis is absent or unrecognised.
 */
export function feeUnitFor(basis: string | null | undefined): string {
  return basis && basis in FEE_UNIT_BASIS_UNIT
    ? FEE_UNIT_BASIS_UNIT[basis as FeeUnitBasis]
    : FEE_UNIT_BASIS_UNIT.built_up_area_sqm
}

/** Just the measurement, for naming what is missing when a property has no area. */
export const FEE_UNIT_BASIS_MEASUREMENT: Record<FeeUnitBasis, string> = {
  built_up_area_sqft: 'built-up area',
  built_up_area_sqm: 'built-up area',
  actual_area_sqft: 'actual area',
  actual_area_sqm: 'actual area',
  common_area_sqm: 'common area',
  shared_area_sqm: 'shared area',
}

// What a fee is measured against — and, for a flat fee, the investor share the
// amount is apportioned by. `flat_only` is the one basis that means "no base":
// the amount is charged whole, per event.
export const FEE_VALUE_BASIS = {
  PURCHASE_COST: 'purchase_cost',
  INVESTMENT_VALUE: 'investment_value',
  GROSS_ORDER_AMOUNT: 'gross_order_amount',
  GROSS_APPRECIATION_AMOUNT: 'gross_appreciation_amount',
  RENTAL_INCOME: 'rental_income',
  FLAT_ONLY: 'flat_only',
} as const

export type FeeValueBasis = (typeof FEE_VALUE_BASIS)[keyof typeof FEE_VALUE_BASIS]

export const FEE_VALUE_BASIS_LABEL: Record<FeeValueBasis, string> = {
  purchase_cost: 'Purchase cost',
  investment_value: 'Investment value',
  gross_order_amount: 'Gross order amount',
  gross_appreciation_amount: 'Gross appreciation',
  rental_income: 'Rental income',
  flat_only: 'Flat only',
}

/**
 * What each basis means for the admin choosing one. Two readings, because the
 * value type changes the question: for a rate it is what the rate is taken
 * against, and for a flat amount it is whose share of the property-level cost
 * is owed.
 */
export const FEE_VALUE_BASIS_DESCRIPTION: Record<FeeValueBasis, string> = {
  purchase_cost: "The whole property's purchase price, charged whole to a single payer.",
  investment_value:
    "The investor's stake in the property at the transaction — a rate is taken against it, a flat amount is scaled by the stake bought.",
  gross_order_amount: 'The order itself: purchase price plus all fees on it.',
  gross_appreciation_amount: "The investor's own profit at exit — nothing is charged on a loss.",
  rental_income: "The investor's share of collected rent, by the stake held when the cost accrues.",
  flat_only: 'No base — the amount is charged whole, every time the fee applies.',
}

export const FEE_SNAPSHOT_SOURCE = {
  DEFAULT_FROM_MASTER: 'default_from_master',
  ADMIN_OVERRIDE: 'admin_override',
  AUTOMATED_RESOLUTION: 'automated_resolution',
} as const

export type FeeSnapshotSource = (typeof FEE_SNAPSHOT_SOURCE)[keyof typeof FEE_SNAPSHOT_SOURCE]

export const FEE_SNAPSHOT_SOURCE_LABEL: Record<FeeSnapshotSource, string> = {
  default_from_master: 'Market default',
  admin_override: 'Admin override',
  automated_resolution: 'Automated',
}

// Option lists for the pickers. Built off the label maps so a new enum value
// only has to be added in one place.
function toOptions<T extends string>(labels: Record<T, string>): { label: string; value: T }[] {
  return (Object.keys(labels) as T[]).map((value) => ({ label: labels[value], value }))
}

export const FEE_LIFECYCLE_STAGE_OPTIONS = toOptions(FEE_LIFECYCLE_STAGE_LABEL)
export const FEE_PARTY_ROLE_OPTIONS = toOptions(FEE_PARTY_ROLE_LABEL)
export const FEE_RECURRENCE_OPTIONS = toOptions(FEE_RECURRENCE_LABEL)
export const FEE_VALUE_TYPE_OPTIONS = toOptions(FEE_VALUE_TYPE_LABEL)
export const FEE_UNIT_BASIS_OPTIONS = toOptions(FEE_UNIT_BASIS_LABEL)

/**
 * The bases each value type may be paired with — mirrors admin-service
 * `modules/masters/fees-master/config/fee-value-basis.config.ts`, which rejects
 * any other combination.
 *
 * A percentage needs something to be a percentage of, so it takes every basis
 * except `flat_only`. A flat amount is already a figure, so its basis answers a
 * different question — whose share is owed — and only the two apportioning
 * bases, or `flat_only` for "charged whole", make sense.
 *
 * A rate always produces a property-level amount — the rate times the area — so
 * its basis has to describe an apportionment too. That rules out `flat_only`
 * (no split at all) and the two already-investor-scoped bases, which the API
 * rejects for the same reason.
 */
export const FEE_VALUE_BASES_BY_VALUE_TYPE: Record<FeeValueType, FeeValueBasis[]> = {
  percentage: [
    FEE_VALUE_BASIS.PURCHASE_COST,
    FEE_VALUE_BASIS.INVESTMENT_VALUE,
    FEE_VALUE_BASIS.GROSS_ORDER_AMOUNT,
    FEE_VALUE_BASIS.GROSS_APPRECIATION_AMOUNT,
    FEE_VALUE_BASIS.RENTAL_INCOME,
  ],
  flat: [
    FEE_VALUE_BASIS.INVESTMENT_VALUE,
    FEE_VALUE_BASIS.RENTAL_INCOME,
    FEE_VALUE_BASIS.FLAT_ONLY,
  ],
  rate_per_unit: [FEE_VALUE_BASIS.INVESTMENT_VALUE, FEE_VALUE_BASIS.RENTAL_INCOME],
}

/** A rate is the one value type that also has to name the area it is charged against. */
export function requiresUnitBasis(valueType: FeeValueType): boolean {
  return valueType === FEE_VALUE_TYPE.RATE_PER_UNIT
}

export function feeValueBasisOptionsFor(
  valueType: FeeValueType,
): { label: string; value: FeeValueBasis }[] {
  return FEE_VALUE_BASES_BY_VALUE_TYPE[valueType].map((value) => ({
    label: FEE_VALUE_BASIS_LABEL[value],
    value,
  }))
}
